import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/Input';
import { useRegistrationStore } from '@/stores/registrationStore';
import { useUserStore } from '@/stores/userStore';
import { useToast } from '@/stores/uiStore';
import { registerParticipant, resendVerificationEmail } from '@/services/registration';

type Step = 'personal' | 'event' | 'kids' | 'review' | 'success';

const EVENT_ENDED = true; // flip to false when HWR 2027 registration opens

export default function RegistrationPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { formData, reset } = useRegistrationStore();
  const { setParticipant } = useUserStore();

  const [currentStep, setCurrentStep] = useState<Step>('personal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredParticipantId, setRegisteredParticipantId] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Form state
  const [personalInfo, setPersonalInfo] = useState({
    fullName: formData.fullName || '',
    age: formData.age || '',
    contactNumber: formData.contactNumber || '',
    email: formData.email || '',
  });

  const [eventInfo, setEventInfo] = useState({
    attendanceDays: formData.attendanceDays || [],
    isFeastMagnolia: formData.isFeastMagnolia ?? true,
    otherFeast: formData.otherFeast || '',
    feastType: (formData.isFeastMagnolia ? 'magnolia' : (formData.otherFeast ? 'other' : 'magnolia')) as 'magnolia' | 'other' | 'none',
    howDidYouLearn: formData.howDidYouLearn || 'social_media',
    friendName: formData.friendName || '',
  });

  const [kidsInfo, setKidsInfo] = useState({
    bringingKids: formData.bringingKids ?? false,
    kidsCount: formData.kidsCount || 1,
    kidsDetails: formData.kidsDetails || '',
  });

  if (EVENT_ENDED) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream to-white flex flex-col items-center justify-center text-center px-6 py-16">
        <h1 className="text-3xl font-serif font-bold text-earth-brown mb-3">
          Registration is Closed
        </h1>
        <p className="text-earth-brown/70 mb-6 max-w-sm">
          Greater Heights HWR 2026 has concluded. Thank you to everyone who joined us!
        </p>
        <p className="text-earth-brown/60 font-medium mb-8">
          Stay tuned for Holy Week Retreat 2027 —{' '}
          <em className="text-crimson">Take Courage</em>
        </p>
        <Link to="/" className="text-sm text-earth-brown/40 underline hover:text-earth-brown/70 transition-colors">
          Back to Home
        </Link>
      </div>
    );
  }

  // Validation
  const validatePersonalInfo = () => {
    const newErrors: Record<string, string> = {};

    if (!personalInfo.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!personalInfo.age || parseInt(String(personalInfo.age)) < 13 || parseInt(String(personalInfo.age)) > 100) {
      newErrors.age = 'Age must be between 13 and 100';
    }

    if (!personalInfo.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^[0-9]{10,11}$/.test(personalInfo.contactNumber.replace(/[-\s]/g, ''))) {
      newErrors.contactNumber = 'Please enter a valid phone number';
    }

    if (!personalInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalInfo.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEventInfo = () => {
    const newErrors: Record<string, string> = {};

    if (eventInfo.attendanceDays.length === 0) {
      newErrors.attendanceDays = 'Please select at least one day';
    }

    if (eventInfo.feastType === 'other' && !eventInfo.otherFeast.trim()) {
      newErrors.otherFeast = 'Please specify which Feast you attend';
    }

    if (eventInfo.howDidYouLearn === 'friend' && !eventInfo.friendName.trim()) {
      newErrors.friendName = 'Please enter your friend\'s name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateKidsInfo = () => {
    const newErrors: Record<string, string> = {};

    if (kidsInfo.bringingKids) {
      if (!kidsInfo.kidsDetails.trim()) {
        newErrors.kidsDetails = 'Please enter kids names and ages';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const goToNext = () => {
    if (currentStep === 'personal' && validatePersonalInfo()) {
      setCurrentStep('event');
    } else if (currentStep === 'event' && validateEventInfo()) {
      if (kidsInfo.bringingKids) {
        setCurrentStep('kids');
      } else {
        setCurrentStep('review');
      }
    } else if (currentStep === 'kids' && validateKidsInfo()) {
      setCurrentStep('review');
    }
  };

  const goToPrevious = () => {
    if (currentStep === 'review') {
      if (kidsInfo.bringingKids) {
        setCurrentStep('kids');
      } else {
        setCurrentStep('event');
      }
    } else if (currentStep === 'kids') {
      setCurrentStep('event');
    } else if (currentStep === 'event') {
      setCurrentStep('personal');
    }
  };

  // Submit
  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const registrationData = {
        fullName: personalInfo.fullName,
        age: parseInt(String(personalInfo.age)),
        contactNumber: personalInfo.contactNumber,
        email: personalInfo.email,
        attendanceDays: eventInfo.attendanceDays as ('day1' | 'day2')[],
        isFeastMagnolia: eventInfo.feastType === 'magnolia',
        otherFeast: eventInfo.feastType === 'other' ? eventInfo.otherFeast : eventInfo.feastType === 'none' ? 'Not a Feast attendee' : '',
        howDidYouLearn: eventInfo.howDidYouLearn as 'social_media' | 'feast' | 'friend',
        friendName: eventInfo.friendName,
        bringingKids: kidsInfo.bringingKids,
        kidsCount: kidsInfo.bringingKids ? kidsInfo.kidsCount : undefined,
        kidsDetails: kidsInfo.bringingKids ? kidsInfo.kidsDetails : undefined,
      };

      const result = await registerParticipant(registrationData);

      if (result.success) {
        // Store participant data
        if (result.participantId) {
          setRegisteredParticipantId(result.participantId);
          setParticipant({
            id: result.participantId,
            fullName: personalInfo.fullName,
            email: personalInfo.email,
            age: parseInt(String(personalInfo.age)),
            isFeaster: eventInfo.isFeastMagnolia,
            checkInPin: result.checkInPin || '0000',
            registrationTimestamp: new Date().toISOString(),
            emailVerified: false,
            status: 'Pending',
          });
        }

        // Save email and reset resend state for success screen
        setRegisteredEmail(personalInfo.email);
        setResendSuccess(false);

        // Show success step
        setCurrentStep('success');
        reset();
      } else {
        toast.error(result.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepConfig: Record<Step, { title: string; step: number }> = {
    personal: { title: 'Personal Information', step: 1 },
    event: { title: 'Event Details', step: 2 },
    kids: { title: 'Kids Information', step: 3 },
    review: { title: 'Review & Submit', step: kidsInfo.bringingKids ? 4 : 3 },
    success: { title: 'Success', step: kidsInfo.bringingKids ? 5 : 4 },
  };

  const totalSteps = kidsInfo.bringingKids ? 4 : 3;
  const showProgressIndicator = currentStep !== 'success';

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-cream/50">
      {/* Return to Home Button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-lg hover:bg-white text-earth-brown rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 group border border-earth-tan/20"
        >
          <svg 
            className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="font-medium text-sm">Back to Home</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-20 md:py-32">
        {/* Header - Hide on success */}
        {showProgressIndicator && (
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-crimson/10 text-crimson text-sm font-semibold mb-6">
            <div className="w-2 h-2 rounded-full bg-crimson animate-pulse" />
            FREE EVENT
          </div>
          <h1 className="text-5xl md:text-6xl font-display font-bold text-earth-brown mb-4 tracking-tight">
            Registration
          </h1>
          <p className="text-lg text-earth-brown/60 max-w-2xl mx-auto">
            Join us for Greater Heights - Holy Week Retreat 2026
          </p>
        </div>
        )}

        {/* Modern Progress Indicator - Hide on success */}
        {showProgressIndicator && (
        <div className="mb-16">
          <div className="relative">
            {/* Progress Bar Background */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-earth-tan/30" />
            
            {/* Progress Bar Fill */}
            <div 
              className="absolute top-5 left-0 h-0.5 bg-gradient-to-r from-crimson to-crimson-light transition-all duration-500 ease-out"
              style={{ width: `${((stepConfig[currentStep].step - 1) / (totalSteps - 1)) * 100}%` }}
            />

            {/* Step Indicators */}
            <div className="relative flex justify-between">
              {[...Array(totalSteps)].map((_, i) => {
                const isActive = stepConfig[currentStep].step === i + 1;
                const isCompleted = stepConfig[currentStep].step > i + 1;

                return (
                  <div key={i} className="flex flex-col items-center">
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        isCompleted
                          ? 'bg-crimson text-white shadow-glow scale-100'
                          : isActive
                          ? 'bg-crimson text-white shadow-large scale-110 ring-4 ring-crimson/20'
                          : 'bg-white text-earth-brown/40 shadow-soft border-2 border-earth-tan/30'
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span>{i + 1}</span>
                      )}
                    </div>
                    <p
                      className={`mt-3 text-xs font-semibold transition-colors duration-300 text-center ${
                        isActive ? 'text-crimson' : isCompleted ? 'text-earth-brown' : 'text-earth-brown/40'
                      }`}
                    >
                      {['Personal', 'Event Details', 'Kids', 'Review'][i]}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current Step Title */}
          <div className="text-center mt-8">
            <p className="text-sm text-earth-brown/50 font-medium uppercase tracking-widest">
              Step {stepConfig[currentStep].step} of {totalSteps}
            </p>
            <h2 className="text-2xl font-display font-bold text-earth-brown mt-2">
              {stepConfig[currentStep].title}
            </h2>
          </div>
        </div>
        )}

        {/* Form Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-large border border-white p-8 md:p-12">
          {/* Step 1: Personal Info */}
          {currentStep === 'personal' && (
            <div className="space-y-6">
              <Input
                label="Full Name"
                value={personalInfo.fullName}
                onChange={(e) => setPersonalInfo({ ...personalInfo, fullName: e.target.value })}
                error={errors.fullName}
                required
                placeholder="Juan Dela Cruz"
                className="bg-white"
              />

              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="Age"
                  type="number"
                  value={personalInfo.age}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, age: e.target.value })}
                  error={errors.age}
                  required
                  placeholder="25"
                  helperText="Must be 13+"
                  className="bg-white"
                />

                <Input
                  label="Contact Number"
                  type="tel"
                  value={personalInfo.contactNumber}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, contactNumber: e.target.value })}
                  error={errors.contactNumber}
                  required
                  placeholder="09123456789"
                  className="bg-white"
                />
              </div>

              <Input
                label="Email Address"
                type="email"
                value={personalInfo.email}
                onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                error={errors.email}
                required
                placeholder="juan@example.com"
                helperText="Your ticket will be sent here"
                className="bg-white"
              />
            </div>
          )}

          {/* Step 2: Event Info */}
          {currentStep === 'event' && (
            <div className="space-y-8">
              {/* Attendance Days */}
              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  What days will you attend? <span className="text-crimson">*</span>
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="checkbox"
                      checked={eventInfo.attendanceDays.includes('day1')}
                      onChange={(e) => {
                        const days = e.target.checked
                          ? [...eventInfo.attendanceDays, 'day1']
                          : eventInfo.attendanceDays.filter((d) => d !== 'day1');
                        setEventInfo({ ...eventInfo, attendanceDays: days as ('day1' | 'day2')[] });
                      }}
                      className="w-5 h-5 rounded-md border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-earth-brown">Day 1</p>
                      <p className="text-sm text-earth-brown/60">April 2, 2026 (Thu)</p>
                    </div>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="checkbox"
                      checked={eventInfo.attendanceDays.includes('day2')}
                      onChange={(e) => {
                        const days = e.target.checked
                          ? [...eventInfo.attendanceDays, 'day2']
                          : eventInfo.attendanceDays.filter((d) => d !== 'day2');
                        setEventInfo({ ...eventInfo, attendanceDays: days as ('day1' | 'day2')[] });
                      }}
                      className="w-5 h-5 rounded-md border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-earth-brown">Day 2</p>
                      <p className="text-sm text-earth-brown/60">April 3, 2026 (Fri)</p>
                    </div>
                  </label>
                </div>
                {errors.attendanceDays && (
                  <p className="text-sm text-red-600 mt-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.attendanceDays}
                  </p>
                )}
              </div>

              {/* Feast Attendee */}
              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  Are you a Feast attendee? <span className="text-crimson">*</span>
                </label>
                <div className="space-y-3">
                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.feastType === 'magnolia'}
                      onChange={() => setEventInfo({ ...eventInfo, feastType: 'magnolia', isFeastMagnolia: true, otherFeast: '' })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Yes, I&apos;m from Feast Magnolia</span>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.feastType === 'other'}
                      onChange={() => setEventInfo({ ...eventInfo, feastType: 'other', isFeastMagnolia: false })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Yes, I attend another Feast</span>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.feastType === 'none'}
                      onChange={() => setEventInfo({ ...eventInfo, feastType: 'none', isFeastMagnolia: false, otherFeast: '' })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Not attending Feast</span>
                  </label>
                </div>

                {eventInfo.feastType === 'other' && (
                  <div className="mt-4">
                    <Input
                      label="Which Feast do you attend?"
                      value={eventInfo.otherFeast}
                      onChange={(e) => setEventInfo({ ...eventInfo, otherFeast: e.target.value })}
                      error={errors.otherFeast}
                      placeholder="e.g., Feast Ortigas"
                      className="bg-white"
                    />
                  </div>
                )}
              </div>

              {/* How did you learn */}
              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  How did you learn about this event? <span className="text-crimson">*</span>
                </label>
                <div className="space-y-3">
                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.howDidYouLearn === 'social_media'}
                      onChange={() => setEventInfo({ ...eventInfo, howDidYouLearn: 'social_media', friendName: '' })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Social Media</span>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.howDidYouLearn === 'feast'}
                      onChange={() => setEventInfo({ ...eventInfo, howDidYouLearn: 'feast', friendName: '' })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">The Feast announcement</span>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={eventInfo.howDidYouLearn === 'friend'}
                      onChange={() => setEventInfo({ ...eventInfo, howDidYouLearn: 'friend' })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Friend&apos;s Invitation</span>
                  </label>
                </div>

                {eventInfo.howDidYouLearn === 'friend' && (
                  <div className="mt-4">
                    <Input
                      label="Who invited you?"
                      value={eventInfo.friendName}
                      onChange={(e) => setEventInfo({ ...eventInfo, friendName: e.target.value })}
                      error={errors.friendName}
                      placeholder="Friend's full name"
                      className="bg-white"
                    />
                  </div>
                )}
              </div>

              {/* Bringing Kids */}
              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  Will you be bringing kids (12 years and below)?
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={kidsInfo.bringingKids === true}
                      onChange={() => setKidsInfo({ ...kidsInfo, bringingKids: true })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">Yes</span>
                  </label>

                  <label className="relative flex items-center gap-4 p-5 bg-white rounded-2xl border-2 border-earth-tan/30 cursor-pointer transition-all hover:border-crimson/50 hover:shadow-soft has-[:checked]:border-crimson has-[:checked]:bg-crimson/5 has-[:checked]:shadow-medium">
                    <input
                      type="radio"
                      checked={kidsInfo.bringingKids === false}
                      onChange={() => setKidsInfo({ ...kidsInfo, bringingKids: false, kidsDetails: '', kidsCount: 1 })}
                      className="w-5 h-5 border-2 border-earth-tan/50 text-crimson focus:ring-2 focus:ring-crimson focus:ring-offset-2"
                    />
                    <span className="font-medium text-earth-brown">No</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Kids Info */}
          {currentStep === 'kids' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  How many kids will you bring? <span className="text-crimson">*</span>
                </label>
                <select
                  value={kidsInfo.kidsCount}
                  onChange={(e) => setKidsInfo({ ...kidsInfo, kidsCount: parseInt(e.target.value) })}
                  className="w-full px-5 py-4 rounded-2xl border-2 border-earth-tan/30 bg-white text-earth-brown font-medium focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-all"
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <option key={num} value={num}>
                      {num} {num === 1 ? 'child' : 'children'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-earth-brown mb-4">
                  Kids Names and Ages <span className="text-crimson">*</span>
                </label>
                <textarea
                  value={kidsInfo.kidsDetails}
                  onChange={(e) => setKidsInfo({ ...kidsInfo, kidsDetails: e.target.value })}
                  rows={5}
                  className="w-full px-5 py-4 rounded-2xl border-2 border-earth-tan/30 bg-white text-earth-brown placeholder-earth-brown/40 focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-all resize-none font-mono text-sm"
                  placeholder="Ella Cruz, 5; John Smith, 8"
                />
                {errors.kidsDetails && (
                  <p className="text-sm text-red-600 mt-3 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.kidsDetails}
                  </p>
                )}
                <p className="text-sm text-earth-brown/50 mt-3 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Format: Name, Age; Name, Age (separate with semicolon)
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-crimson/5 to-crimson/10 rounded-2xl p-6 border border-crimson/20">
                <h3 className="text-lg font-bold text-earth-brown mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-crimson" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Personal Information
                </h3>
                <div className="space-y-3">
                  <ReviewItem label="Full Name" value={personalInfo.fullName} />
                  <ReviewItem label="Age" value={String(personalInfo.age)} />
                  <ReviewItem label="Contact Number" value={personalInfo.contactNumber} />
                  <ReviewItem label="Email" value={personalInfo.email} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-crimson/5 to-crimson/10 rounded-2xl p-6 border border-crimson/20">
                <h3 className="text-lg font-bold text-earth-brown mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-crimson" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                  Event Details
                </h3>
                <div className="space-y-3">
                  <ReviewItem 
                    label="Attending Days" 
                    value={eventInfo.attendanceDays.map(d => d === 'day1' ? 'Day 1 (Apr 2)' : 'Day 2 (Apr 3)').join(', ')} 
                  />
                  <ReviewItem
                    label="Feast Attendee"
                    value={eventInfo.feastType === 'magnolia' ? 'Feast Magnolia' : eventInfo.feastType === 'other' ? eventInfo.otherFeast : 'Not attending Feast'}
                  />
                  <ReviewItem 
                    label="How You Learned" 
                    value={
                      eventInfo.howDidYouLearn === 'social_media' 
                        ? 'Social Media' 
                        : eventInfo.howDidYouLearn === 'feast'
                        ? 'The Feast'
                        : `Friend (${eventInfo.friendName})`
                    } 
                  />
                </div>
              </div>

              {kidsInfo.bringingKids && (
                <div className="bg-gradient-to-br from-crimson/5 to-crimson/10 rounded-2xl p-6 border border-crimson/20">
                  <h3 className="text-lg font-bold text-earth-brown mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-crimson" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                    Kids Information
                  </h3>
                  <div className="space-y-3">
                    <ReviewItem label="Number of Kids" value={`${kidsInfo.kidsCount} ${kidsInfo.kidsCount === 1 ? 'child' : 'children'}`} />
                    <ReviewItem label="Kids Details" value={kidsInfo.kidsDetails} mono />
                  </div>
                </div>
              )}

              <div className="bg-cream/50 rounded-2xl p-6 border-l-4 border-crimson">
                <p className="text-sm text-earth-brown/70 leading-relaxed">
                  <strong className="text-earth-brown">Important:</strong> By submitting this registration, you confirm that all information provided is accurate.
                  You will receive a confirmation email with your event ticket within a few minutes.
                </p>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {currentStep === 'success' && (
            <SuccessStep
              email={registeredEmail}
              participantId={registeredParticipantId}
              isResending={isResending}
              resendSuccess={resendSuccess}
              onResend={async () => {
                if (!registeredParticipantId || isResending) return;
                setIsResending(true);
                try {
                  const result = await resendVerificationEmail(registeredParticipantId);
                  if (result.success) {
                    setResendSuccess(true);
                    toast.success('Verification email resent!');
                  } else {
                    toast.error(result.message || 'Failed to resend email');
                  }
                } catch {
                  toast.error('Failed to resend email. Please try again.');
                } finally {
                  setIsResending(false);
                }
              }}
              onRegisterAnother={() => {
                setCurrentStep('personal');
                setRegisteredEmail('');
                setRegisteredParticipantId('');
                setResendSuccess(false);
                setPersonalInfo({ fullName: '', age: '', contactNumber: '', email: '' });
                setEventInfo({
                  attendanceDays: [],
                  isFeastMagnolia: true,
                  otherFeast: '',
                  feastType: 'magnolia',
                  howDidYouLearn: 'social_media',
                  friendName: '',
                });
                setKidsInfo({ bringingKids: false, kidsCount: 1, kidsDetails: '' });
              }}
            />
          )}

          {/* Navigation Buttons - Hide on success step */}
          {currentStep !== 'success' && (
          <div className="flex gap-4 mt-10">
            {currentStep !== 'personal' && (
              <button
                onClick={goToPrevious}
                className="flex-1 px-6 py-4 bg-white text-earth-brown font-semibold rounded-2xl border-2 border-earth-tan/50 hover:border-earth-tan hover:bg-earth-tan/5 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
            )}

            {currentStep !== 'review' ? (
<button 
  onClick={goToNext}
  className="flex-1 px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 flex items-center justify-center gap-2 active:scale-95"
>
                Next Step
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
<button 
  onClick={handleSubmit}
  disabled={isSubmitting}
  className="flex-1 px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
>
                {isSubmitting ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Complete Registration
                  </>
                )}
              </button>
            )}
          </div>
          )}
        </div>

        {/* Help Text - Hide on success step */}
        {currentStep !== 'success' && (
        <div className="mt-8 text-center">
          <p className="text-sm text-earth-brown/50">
            Need assistance? Contact us at{' '}
            <a href="mailto:feastmagnoliahwr@gmail.com" className="text-crimson hover:underline font-semibold">
              feastmagnoliahwr@gmail.com
            </a>
          </p>
        </div>
        )}
      </div>
    </div>
  );
}

// Review Components
const ReviewItem = ({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex justify-between items-start gap-4 py-2">
    <span className="text-sm text-earth-brown/60 font-medium">{label}:</span>
    <span className={`text-sm text-earth-brown font-semibold text-right ${mono ? 'font-mono' : ''}`}>
      {value}
    </span>
  </div>
);

// Success Step Component
const SuccessStep = ({
  email,
  participantId,
  isResending,
  resendSuccess,
  onResend,
  onRegisterAnother,
}: {
  email: string;
  participantId: string;
  isResending: boolean;
  resendSuccess: boolean;
  onResend: () => void;
  onRegisterAnother: () => void;
}) => (
  <div className="text-center space-y-8">
    {/* Success Icon */}
    <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center">
      <svg className="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    </div>

    <div>
      <h2 className="text-3xl font-display font-bold text-earth-brown mb-4">
        Registration Submitted!
      </h2>
      <p className="text-lg text-earth-brown/70 leading-relaxed">
        We&apos;ve sent a verification email to:
      </p>
      <p className="text-xl font-semibold text-crimson mt-2 mb-4">
        {email}
      </p>
    </div>

    {/* Email Instructions */}
    <div className="bg-gradient-to-br from-crimson/5 to-crimson/10 rounded-2xl p-6 border border-crimson/20 text-left">
      <h3 className="text-lg font-bold text-earth-brown mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-crimson" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
        </svg>
        What&apos;s Next?
      </h3>
      <ol className="space-y-3 text-earth-brown/80">
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-crimson text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
          <span>Check your email inbox (and spam folder)</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-crimson text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
          <span>Click the verification link in the email</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-crimson text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
          <span>Receive your QR code ticket via email</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="flex-shrink-0 w-6 h-6 bg-crimson text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
          <span>Save your ticket image for the event</span>
        </li>
      </ol>
    </div>

    {/* Action Buttons */}
    <div className="space-y-4">
      <button
        onClick={onResend}
        disabled={isResending || resendSuccess}
        className="w-full px-6 py-4 bg-crimson text-white font-semibold rounded-2xl hover:bg-crimson-dark hover:shadow-large transition-all duration-300 flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isResending ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Sending...
          </>
        ) : resendSuccess ? (
          <>
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Email Sent!
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Resend Verification Email
          </>
        )}
      </button>
      <button
        onClick={onRegisterAnother}
        className="w-full px-6 py-4 bg-white text-earth-brown font-semibold rounded-2xl border-2 border-earth-tan/50 hover:border-earth-tan hover:bg-earth-tan/5 transition-all duration-300"
      >
        Register Another Person
      </button>
    </div>

    {/* Help Note */}
    <p className="text-sm text-earth-brown/50">
      Didn&apos;t receive the email? Check your spam folder or contact{' '}
      <a href="mailto:feastmagnoliahwr@gmail.com" className="text-crimson hover:underline font-semibold">
        feastmagnoliahwr@gmail.com
      </a>
    </p>

    {/* Debug info - remove in production */}
    {participantId && (
      <p className="text-xs text-earth-brown/30">
        Registration ID: {participantId.slice(0, 8)}...
      </p>
    )}
  </div>
);